import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { getLeadToClosed } from "../../store/modules/analytics/analyticsApi";

interface LeadToClosedChartProps {
  /** Real year ids only; the Pipeline has no closing dates to measure */
  yearIds: number[];
  transactionTypeIds: number[];
  coAgentIds: number[];
}

const monthLabel = (months: number) =>
  months === 0 ? "< 1 mo" : `${months} mo`;

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** Number of transactions by Lead-to-Closed time (Closing Date - Lead Received Date) in months */
export const LeadToClosedChart: React.FC<LeadToClosedChartProps> = ({
  yearIds,
  transactionTypeIds,
  coAgentIds,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, isLoading } = useQuery({
    queryKey: ["leadToClosed", yearIds, transactionTypeIds, coAgentIds],
    queryFn: () => getLeadToClosed(yearIds, transactionTypeIds, coAgentIds),
    refetchOnWindowFocus: false,
  });

  // Continuous month axis: empty months show as gaps instead of disappearing
  const chartData = useMemo(() => {
    const buckets = data?.buckets ?? [];
    if (buckets.length === 0) return [];
    const maxMonths = Math.max(...buckets.map((b) => b.months));
    return Array.from({ length: maxMonths + 1 }, (_, months) => {
      const bucket = buckets.find((b) => b.months === months);
      return {
        label: monthLabel(months),
        count: bucket?.count ?? 0,
        volume: bucket?.volume ?? 0,
      };
    });
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {(data?.excludedCount ?? 0) > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data!.excludedCount}{" "}
          {data!.excludedCount === 1 ? "transaction has" : "transactions have"}{" "}
          no Lead Received Date and {data!.excludedCount === 1 ? "is" : "are"}{" "}
          not included.
        </Alert>
      )}
      {chartData.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          No transactions with both a Lead Received Date and a Closing Date in
          the selected years.
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 20, left: 0, bottom: 30 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e0e0e0"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              interval={isMobile ? "preserveStartEnd" : 0}
              tick={{ fontSize: isMobile ? 10 : 12, fill: "#666" }}
              label={{
                value: "Lead-to-Closed Time",
                position: "insideBottom",
                offset: -20,
                fill: "#666",
                fontSize: 12,
              }}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: isMobile ? 10 : 12, fill: "#666" }}
              label={{
                value: "Transactions",
                angle: -90,
                position: "insideLeft",
                offset: 15,
                fill: "#666",
                fontSize: 12,
              }}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              formatter={(value, _name, item) => [
                `${value} (${currency.format(item.payload.volume)})`,
                "Transactions",
              ]}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
            <Bar
              dataKey="count"
              fill={theme.palette.primary.main}
              radius={[4, 4, 0, 0]}
              maxBarSize={48}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </>
  );
};

export default LeadToClosedChart;
